import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Group, Stack, Text } from '@mantine/core';
import { ChevronDown, ChevronUp, Edit3 } from 'react-feather';
import { notifications } from '@mantine/notifications';
import {
  RichTextEditor,
  LINK_CLICKABILITY_HELPER_TEXT,
  type RichTextControl,
} from '../../../../components/common/RichTextEditor/RichTextEditor';
import { coachActiveSessionApi } from '../../../../services/api/coachActiveSession';
import { POST_SESSION_PRIMARY_BG } from '../../../../components/session/postSessionCard';
import { ContentCard } from '../../../../components/common/ContentCard';

/**
 * MED-794 AC4 / MED-1050 — "Your Session Notes" card (coach + peer-leader
 * post-session; this is one shared component used by both roles).
 *
 * Carries forward the in-session notes captured during the active session
 * (MED-792); persistence reuses `coachActiveSessionApi.updateCoachNotes`
 * (unchanged). MED-1050 polish:
 *   - Card uses the shared post-session treatment (rounded 12 + soft shadow).
 *   - Header: small inline notes icon + title + subtitle; the top-right slot is
 *     reserved for the future "Clean Up" AI action (MED-893, AC7) — not built.
 *   - Edit mode uses the MED-1014 reduced toolbar (B I U | bullet · ordered |
 *     link) and the in-session Saving… / Saved indicator (no read-mode
 *     timestamp, AC4). Single Edit action — "Add more" removed (AC5).
 */

// MED-1050 (QA) — autosave debounce while editing, matching the in-session
// notes panel (ActiveSessionNotesPanel) so typing stays instant.
const SAVE_DEBOUNCE_MS = 600;

// MED-1014 reduced toolbar — matches the in-session notes editor.
const NOTES_TOOLBAR: RichTextControl[][] = [
  ['bold', 'italic', 'underline'],
  ['bulletList', 'orderedList'],
  ['link'],
];

const TITLE_COLOR = '#1A1A2E';
const SUBTITLE_COLOR = '#9CA3AF';
const NOTES_BG = '#F9FAFB';
const NOTES_BORDER = '#E5E7EB';
const NOTES_TEXT = '#374151';

interface Props {
  sessionId: string;
  initialHtml: string;
  /** Retained for API compatibility; the read-mode timestamp is dropped (AC4). */
  initialUpdatedAt: string | null;
  onSaved?: () => void;
  /**
   * MED-1077 — opt-in manual collapse (right-side chevron). Set on the
   * peer-leader main column where Session Notes is a collapsible section; the
   * coach instance (right column) omits it and stays always-expanded.
   */
  collapsible?: boolean;
}

export function SessionNotesCard({
  sessionId,
  initialHtml,
  onSaved,
  collapsible = false,
}: Props) {
  const [editing, setEditing] = useState<boolean>(false);
  const [html, setHtml] = useState<string>(initialHtml);
  const [saving, setSaving] = useState<boolean>(false);
  const [justSaved, setJustSaved] = useState<boolean>(false);
  // Manual collapse only (loads expanded); the chevron renders when collapsible.
  const [collapsed, setCollapsed] = useState<boolean>(false);
  // MED-1050 (QA) — debounced autosave while editing. lastSentRef guards
  // against redundant PATCHes (e.g. opening edit mode without typing).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string>(initialHtml);

  useEffect(() => {
    // Reset local state when the bundle refreshes with new server data.
    setHtml(initialHtml);
    lastSentRef.current = initialHtml;
  }, [initialHtml]);

  // Cancel any pending autosave on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const persist = async (next: string) => {
    setSaving(true);
    try {
      await coachActiveSessionApi.updateCoachNotes(sessionId, {
        content_html: next,
      });
      lastSentRef.current = next;
      setJustSaved(true);
      onSaved?.();
    } catch (e) {
      notifications.show({
        color: 'red',
        message:
          e instanceof Error ? e.message : 'Failed to save session notes.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setJustSaved(false);
    setEditing(true);
  };

  // MED-1050 (QA) — autosave on change: debounce a save, skipping no-op writes.
  const handleChange = (next: string) => {
    setHtml(next);
    setJustSaved(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (next === lastSentRef.current) return;
    debounceRef.current = setTimeout(
      () => void persist(next),
      SAVE_DEBOUNCE_MS
    );
  };

  // Flush any pending autosave, then return to the read view.
  const handleDone = async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (html !== lastSentRef.current) await persist(html);
    setEditing(false);
  };

  const hasNotes = Boolean(
    html && html.trim() !== '' && html.trim() !== '<p></p>'
  );

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      data-testid="post-session-notes-card"
      header={
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group gap={8} align="center" wrap="nowrap">
              <Edit3 size={18} color={TITLE_COLOR} aria-hidden="true" />
              <Text fz={15} fw={700} c={TITLE_COLOR}>
                Your Session Notes
              </Text>
            </Group>
            <Text fz={12} fw={500} c={SUBTITLE_COLOR}>
              From your in-session notes. Edit before submitting.
            </Text>
          </Stack>
          {collapsible && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setCollapsed(c => !c)}
              aria-label={collapsed ? 'Expand notes' : 'Collapse notes'}
            >
              {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </ActionIcon>
          )}
        </Group>
      }
    >
      {collapsed ? null : editing ? (
        <Stack gap="sm">
          <RichTextEditor
            value={html}
            onChange={handleChange}
            minHeight={180}
            toolbar={NOTES_TOOLBAR}
            placeholder="Add or refine your session notes…"
            helperText={LINK_CLICKABILITY_HELPER_TEXT}
          />
          {/* MED-1050 (QA) — notes autosave on change (no explicit Save). The
              Saving…/Saved indicator mirrors the in-session panel; "Done" flushes
              any pending save and returns to the read view. */}
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box>
              <SaveStatus saving={saving} saved={justSaved} />
            </Box>
            <Button
              size="sm"
              onClick={() => void handleDone()}
              loading={saving}
              styles={{
                root: {
                  backgroundColor: POST_SESSION_PRIMARY_BG,
                  color: '#FFFFFF',
                },
              }}
            >
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Box
            style={{
              backgroundColor: NOTES_BG,
              border: `1px solid ${NOTES_BORDER}`,
              borderRadius: 10,
              padding: '21px 17px 15px',
              minHeight: 80,
            }}
          >
            {hasNotes ? (
              <Box
                style={{ color: NOTES_TEXT, fontSize: 13, lineHeight: 1.65 }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : (
              <Text c="dimmed" fs="italic" fz={13}>
                No in-session notes were captured. Click Edit to start writing.
              </Text>
            )}
          </Box>
          <Group justify="flex-end" pt={2}>
            <Button
              size="sm"
              leftSection={<Edit3 size={14} />}
              onClick={handleEdit}
              styles={{
                root: {
                  backgroundColor: POST_SESSION_PRIMARY_BG,
                  color: '#FFFFFF',
                },
              }}
            >
              Edit Notes
            </Button>
          </Group>
        </Stack>
      )}
    </ContentCard>
  );
}

/**
 * MED-1050 AC4 — in-session Saving… / Saved status (no read-mode timestamp):
 * a muted "Saving…" while the request is in flight, then a green-dot "Saved".
 */
function SaveStatus({ saving, saved }: { saving: boolean; saved: boolean }) {
  if (saving) {
    return (
      <Text size="xs" c="dimmed">
        Saving…
      </Text>
    );
  }
  if (saved) {
    return (
      <Group gap={6} align="center" wrap="nowrap">
        <Box
          w={8}
          h={8}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-medleyGreen-6)',
          }}
        />
        <Text size="xs" c="dimmed">
          Saved
        </Text>
      </Group>
    );
  }
  return null;
}

export default SessionNotesCard;
